/**
 * Grouped documents (KYB): one upload per group, the required ones first —
 * the current group is "Required now", the rest wait as "Next" (Didit "Upload
 * your business documents"). Files upload as they are picked; the step is sent
 * once every required group has one. The server checks each file belongs to
 * this case and files it as a `kyb_<group>` document.
 */
import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ACCEPTED_TYPES, problemWith, uploadDocumentFile } from './businessDocument';
import { postCollectionFlowEvent } from './client';
import type { CollectionFlowSnapshot } from './types';

export const DOCUMENTS_EVENT = 'DOCUMENTS_SUBMITTED';

export interface DocumentGroup {
  key: string;
  label: string;
  examples: string;
  required: boolean;
}

export interface DocumentGroupsView {
  kind: 'documents';
  groups: DocumentGroup[];
}

export function documentGroupsOf(step: unknown): DocumentGroupsView | null {
  if (typeof step !== 'object' || step === null) return null;
  const s = step as Partial<DocumentGroupsView>;
  return s.kind === 'documents' && Array.isArray(s.groups) ? (s as DocumentGroupsView) : null;
}

/** Required groups first (in the step's order), then the optional ones. */
export function ordered(groups: DocumentGroup[]): DocumentGroup[] {
  return [...groups.filter((g) => g.required), ...groups.filter((g) => !g.required)];
}

export function DocumentGroupsStep({
  token,
  view,
  onAdvance,
}: {
  token: string;
  view: DocumentGroupsView;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
}) {
  const { t } = useTranslation();
  const groups = ordered(view.groups);
  const input = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<Record<string, string>>({});
  const [skipped, setSkipped] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const current = groups.find((g) => !(g.key in files) && !skipped.includes(g.key)) ?? null;
  const index = current ? groups.indexOf(current) : groups.length;
  const label = (g: DocumentGroup) =>
    g.label || t(`documents.group.${g.key}.label`, { defaultValue: g.key });
  const examples = (g: DocumentGroup) =>
    g.examples || t(`documents.group.${g.key}.examples`, { defaultValue: '' });

  const send = async (next: Record<string, string>) => {
    setBusy(true);
    setProblem(null);
    try {
      onAdvance(await postCollectionFlowEvent(token, DOCUMENTS_EVENT, { files: next }));
    } catch {
      setProblem(t('flow.businessDocument.failed'));
    } finally {
      setBusy(false);
    }
  };

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !current) return;
    const found = problemWith(file);
    if (found) {
      setProblem(t(`flow.businessDocument.${found}`));
      return;
    }
    setBusy(true);
    setProblem(null);
    try {
      const id = await uploadDocumentFile(token, file, `kyb-${current.key}`);
      setFiles({ ...files, [current.key]: id });
    } catch {
      setProblem(t('flow.businessDocument.failed'));
    } finally {
      setBusy(false);
    }
  };

  const allRequired = groups.filter((g) => g.required).every((g) => g.key in files);

  return (
    <div className="details-form">
      <h1 className="step-title">{t('documents.title')}</h1>
      <p className="step-text">
        {t('documents.lead', {
          count: groups.filter((g) => g.required).length,
        })}
      </p>
      <ol className="company-results">
        {groups.map((g, i) => {
          const done = g.key in files;
          const now = current?.key === g.key;
          return (
            <li className="company-result" key={g.key} aria-current={now ? 'step' : undefined}>
              <span className="company-result__body">
                <span className="company-result__name">
                  {i + 1}. {label(g)}
                </span>
                {examples(g) ? <span className="step-text">{examples(g)}</span> : null}
                <span className="company-result__pills">
                  <span className="pill">
                    {done
                      ? t('documents.uploaded')
                      : now
                        ? g.required
                          ? t('documents.requiredNow')
                          : t('documents.optional')
                        : skipped.includes(g.key)
                          ? t('documents.skipped')
                          : t('documents.next')}
                  </span>
                </span>
              </span>
            </li>
          );
        })}
      </ol>
      <input
        accept={ACCEPTED_TYPES}
        hidden
        data-testid="document-group-input"
        onChange={(e) => void onPick(e)}
        ref={input}
        type="file"
      />
      {problem ? (
        <p className="alert" role="alert">
          {problem}
        </p>
      ) : null}
      <div className="actions">
        {current ? (
          <>
            <p className="details-label">
              {index + 1} / {groups.length} · {label(current)}
            </p>
            <button
              className="btn"
              disabled={busy}
              onClick={() => input.current?.click()}
              type="button"
            >
              {busy ? t('flow.businessDocument.uploading') : t('documents.upload')}
            </button>
            {!current.required ? (
              <button
                className="btn btn--ghost"
                onClick={() => setSkipped([...skipped, current.key])}
                type="button"
              >
                {t('documents.skip')}
              </button>
            ) : null}
          </>
        ) : null}
        {!current && allRequired ? (
          <button className="btn" disabled={busy} onClick={() => void send(files)} type="button">
            {t('company.confirm.submit')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
